import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getPinTiers, getMyPins, retryPinIssue } from "../../services/auth/User";
import Loader2 from "../../components/Loader2";
// Shared page chrome (.contain, .boxy, .tab-top, .btn-1) lives in profile.css.
import "../profile/profile.css";
import "./pins.css";

function HybridPins() {
  const [tiers, setTiers] = useState([]);
  const [earnedPins, setEarnedPins] = useState([]);
  const [isPending, setIsPending] = useState(true);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    loadPage();
  }, []);

  const loadPage = async () => {
    setIsPending(true);
    try {
      const [tierRes, pinRes] = await Promise.all([getPinTiers(), getMyPins()]);
      setTiers(tierRes?.tiers || []);
      setEarnedPins(pinRes?.earnedPins || []);
    } catch (error) {
      console.error("Failed to load pins page:", error);
      toast.error("Could not load your hybrid pins.");
    } finally {
      setIsPending(false);
    }
  };

  const handleRetry = async (earnedPinId) => {
    setRetryingId(earnedPinId);
    try {
      const res = await retryPinIssue(earnedPinId);
      if (res?.success) {
        toast.success(`Pin issued: ${res.pin}`);
        await loadPage();
      } else {
        toast.error(res?.message || "Still could not issue the pin.");
      }
    } catch (error) {
      toast.error("Still could not issue the pin. Please try again shortly.");
    } finally {
      setRetryingId(null);
    }
  };

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin).then(() => toast.success("Pin copied"));
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // Tiers arrive highest-first; read low-to-high so the ranges make sense as copy.
  const ascendingTiers = [...tiers].sort((a, b) => a.minAmount - b.minAmount);

  const tierRange = (tier, index) => {
    const next = ascendingTiers[index + 1];
    const from = `₹${tier.minAmount.toLocaleString("en-IN")}`;
    return next ? `${from} – ₹${(next.minAmount - 1).toLocaleString("en-IN")}` : `${from} and above`;
  };

  return (
    <>
      {isPending && <Loader2 />}
      <div className="contain">
        <h1>Hybrid Activation Pins</h1>

        <div className="boxy">
          <div className="tab-top">
            <h3>How You Earn a Pin</h3>
          </div>
          <div className="seperator"></div>
          <div className="tab-body">
            <p className="pin-intro">
              Shop on CGT Homes and your order earns a CryptoGT Hybrid activation pin —
              the more a single order totals, the bigger the package it unlocks. Pins
              appear below as soon as your payment clears, and can be entered on the
              CryptoGT Hybrid page to activate.
            </p>
            <p className="pin-note">
              Pins are earned on orders paid online by card, UPI or netbanking. Orders
              paid with CGT Coins or Cash on Delivery do not earn a pin.
            </p>

            <div className="pin-tier-grid">
              {ascendingTiers.map((tier, index) => (
                <div className="pin-tier" key={tier.packageType}>
                  <span className="pin-tier-range">Order {tierRange(tier, index)}</span>
                  <span className="pin-tier-package">${tier.packageType} Package</span>
                  <span className="pin-tier-bonus">
                    + {tier.bonusCoins.toLocaleString("en-IN")} CGT welcome bonus
                  </span>
                </div>
              ))}
            </div>

            <Link to="/products" className="btn-1 pin-shop-btn">
              Shop Now
            </Link>
          </div>
        </div>

        <div className="boxy" style={{ marginTop: "20px" }}>
          <div className="tab-top">
            <h3>My Pins</h3>
          </div>
          <div className="seperator"></div>
          <div className="tab-body">
            {earnedPins.length === 0 ? (
              <p className="pin-empty">
                You have not earned any pins yet. Place a qualifying order to get one.
              </p>
            ) : (
              <div className="pin-table-wrap">
                <table className="pin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order Total</th>
                      <th>Package</th>
                      <th>Pin</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnedPins.map((earned) => (
                      <tr key={earned._id}>
                        <td>{formatDate(earned.createdAt)}</td>
                        <td>₹{earned.orderAmount.toLocaleString("en-IN")}</td>
                        <td>${earned.packageType}</td>
                        <td>
                          {earned.pin ? (
                            <button
                              type="button"
                              className="pin-code"
                              onClick={() => copyPin(earned.pin)}
                              title="Click to copy"
                            >
                              {earned.pin}
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {earned.pin ? (
                            <span className="pin-status is-success">Issued</span>
                          ) : (
                            <button
                              type="button"
                              className="pin-retry"
                              onClick={() => handleRetry(earned._id)}
                              disabled={retryingId === earned._id}
                            >
                              {retryingId === earned._id ? "Retrying…" : "Retry issue"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default HybridPins;
